<template>
  <div class="header-search">
    <button
      v-if="!isSearchActive"
      class="search-button"
      tabindex="0"
      @click="onClickSearchIcon"
    >
      <w-search-icon color="#A5A5A5" />
      <span class="a11y-hidden">검색</span>
    </button>
    <template v-else>
      <div class="search-box-background"></div>
      <transition name="fade-out">
        <div ref="searchBoxRef" class="search-box-container">
          <w-search-box
            class="w-search-box"
            :value="searchKeyword"
            :transition="!isSearchResult || transition"
            :gradation="true"
            :max-length="MAX_SEARCH_LENGTH"
            @input="onInputSearch"
            @keydown-enter="onKeyDownEnter"
            @delete="onDeleteSearch"
            @focus="onFocusSearchBox"
            @click-gradation="onClickGradation"
          />
        </div>
      </transition>
    </template>
  </div>
</template>

<script setup>
  import WSearchIcon from '@/components/ui/Search/icon/WSearchIcon.vue'
  import WSearchBox from '@/components/ui/Search/WSearchBox.vue'
</script>

<script>
  import _ from 'lodash'
  import { stringify } from 'query-string'
  import {
    SEARCH_TYPING_QUERY,
    SEARCH_RESULT_QUERY,
    SEARCH_AGAIN_INPUT_FOCUS,
    SEARCH_SAME_QUERY,
  } from '@/common/resources/const'
  import { PATH_NAME_SEARCH } from '@/common/resources/router.const'
  import { removeEmptySpace, setSearchWordSave } from '@/common/utils/common'
  import {
    sendEventLogTextSearch,
    sendEventLogDeleteSearchKeyword,
    sendEventLogSearchOpen,
  } from '@/common/utils/log.v2.send'
  const MESSAGE_SEARCH_WORD_LENGTH_LIMIT = `최대 50자까지 입력 가능합니다. 글자수를 확인해주세요.`
  const MESSAGE_SEARCH_WORD_IS_EMPTY = `검색어를 입력 하세요.`
  const MAX_SEARCH_LENGTH = 50
  export default {
    name: 'WHeaderSearch',
    data() {
      return {
        searchKeyword: '',
        isSearchActive: false,
        transition: true,
      }
    },
    computed: {
      isSearchResult() {
        const { searchWord } = this.$route.query
        if (searchWord) {
          this.transition = false
          return true
        }
        return false
      },
    },
    watch: {
      $route(route) {
        const isSearch = route.path === '/search'
        this.isSearchActive = isSearch
        this.searchKeyword = route.query?.searchWord ?? ''
        if (isSearch) {
          this.$events.$emit(SEARCH_RESULT_QUERY, this.searchKeyword)
        }
      },
      isSearchActive(value) {
        this.transition = true
        if (value) {
          document.addEventListener('click', this.onBlurSearchBox, true)
        } else {
          document.removeEventListener('click', this.onBlurSearchBox)
        }
      },
    },
    beforeDestroy() {
      document.removeEventListener('keyup', this.keyup)
    },
    mounted() {
      const currentPath = this.$route.path
      const { searchWord } = this.$route.query
      if (currentPath === '/search') {
        this.isSearchActive = true
        this.$events.$emit(SEARCH_AGAIN_INPUT_FOCUS, {
          isAutoCompleteReady: true,
        })
      }
      if (searchWord) {
        this.searchKeyword = searchWord
        this.$events.$emit(SEARCH_RESULT_QUERY, this.searchKeyword)
      }
    },
    methods: {
      onClickGradation() {
        if (this.searchKeyword?.length > 0) return
        this.isSearchActive = false
      },
      onBlurSearchBox(event) {
        if (this.searchKeyword?.length > 0) return
        if (!this.$refs.searchBoxRef?.contains(event.target))
          this.isSearchActive = false
      },
      onFocusSearchBox() {
        this.$events.$emit(SEARCH_AGAIN_INPUT_FOCUS, {
          isAutoCompleteReady: true,
        })
      },
      async onClickSearchIcon() {
        const CURRENT_URL_NAME = _.get(this.$route, 'name', '')
        const IS_DIFFERENT_ROUTE_LOCATION =
          CURRENT_URL_NAME !== PATH_NAME_SEARCH
        if (IS_DIFFERENT_ROUTE_LOCATION) {
          sendEventLogSearchOpen({ route: this.$route })
          await this.$router.push({
            name: PATH_NAME_SEARCH,
          })
        }
        this.isSearchActive = true
        this.$events.$emit(SEARCH_AGAIN_INPUT_FOCUS, {
          isAutoCompleteReady: true,
        })
      },
      onInputSearch(event) {
        if (event.target.value === this.searchKeyword) return
        if (event.target.value.length > MAX_SEARCH_LENGTH) {
          alert(MESSAGE_SEARCH_WORD_LENGTH_LIMIT)
        }
        this.searchKeyword = event.target.value.slice(0, MAX_SEARCH_LENGTH)
        this._sendAutoSearch(event)
      },
      _sendAutoSearch: _.debounce(function (event) {
        this.sendAutoSearch()
      }, 400),
      sendAutoSearch() {
        this.$events.$emit(SEARCH_TYPING_QUERY, this.searchKeyword)
      },
      onDeleteSearch() {
        const KEYWORD_INITIALIZED = ''
        this.searchKeyword = ''
        this.$events.$emit(SEARCH_TYPING_QUERY, KEYWORD_INITIALIZED)
        this.$events.$emit(SEARCH_RESULT_QUERY, KEYWORD_INITIALIZED)
        sendEventLogDeleteSearchKeyword()
      },
      checkInputValidation(value) {
        if (value?.length > MAX_SEARCH_LENGTH) {
          alert(MESSAGE_SEARCH_WORD_LENGTH_LIMIT)
          return false
        }
        if (value?.length === 0) {
          alert(MESSAGE_SEARCH_WORD_IS_EMPTY)
          return false
        }
        return true
      },
      onKeyDownEnter() {
        this.searchKeyword = removeEmptySpace(this.searchKeyword)
        const isValid = this.checkInputValidation(this.searchKeyword)
        if (!isValid) return
        setSearchWordSave(this.searchKeyword)
        const { searchWord: querySearchWord } = this.$route.query
        if (this.searchKeyword === querySearchWord) {
          this.$events.$emit(SEARCH_SAME_QUERY)
          return
        }
        this.$events.$emit(SEARCH_TYPING_QUERY, '')
        this.$events.$emit(SEARCH_RESULT_QUERY, this.searchKeyword)
        sendEventLogTextSearch({
          keyword: this.searchKeyword,
          landingUrl: `${PATH_NAME_SEARCH}?${stringify({ searchWord: this.searchKeyword })}`,
        })
        this.$router.push({
          name: PATH_NAME_SEARCH,
          query: { searchWord: this.searchKeyword },
        })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .fade-out-leave-to {
    opacity: 0;
  }
  .fade-out-leave-active {
    transition: opacity 0.1s;
  }
  .header-search {
    position: relative;
    height: 22px;
    .search-button {
    }

    .search-box-container {
      position: absolute;
      top: -9px;
      right: 0;
    }
    // search box에 가려진 카테고리 버튼 클릭 방지
    .search-box-background {
      position: absolute;
      top: -26px;
      right: 0;
      width: 420px;
      height: 74px;
      background-color: transparent;
      content: '';
    }
  }
</style>
